import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { COURSES, LOGOS } from "../data/content";
import { useBooking } from "./BookingModal";

const CATS = Object.keys(COURSES); // Certificate, Diploma, Long-Term

const Courses = () => {
  const [tab, setTab] = useState(CATS[0]);
  const { openBooking } = useBooking();

  return (
    <section
      id="academy"
      data-testid="courses-section"
      className="relative bg-[color:var(--bg)] px-6 py-32 md:px-12 md:py-48"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-16 grid grid-cols-1 items-end gap-10 md:mb-20 md:grid-cols-12">
          <div className="md:col-span-8">
            <div className="chip mb-6">
              <span className="dot" />
              03 · Academy
            </div>
            <h2 className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl">
              An academy
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                that trains you
              </span>{" "}
              like an artist.
            </h2>
          </div>
          <div className="flex items-center gap-4 md:col-span-4 md:justify-end">
            <img
              src={LOGOS.iao}
              alt="IAO Accredited"
              className="h-24 w-24 rounded-full object-cover md:h-28 md:w-28"
            />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
                Accredited by
              </div>
              <div className="font-serif text-xl leading-tight">
                International
                <br />
                <span className="italic-serif text-[color:var(--primary)]">
                  Accreditation Org.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[color:var(--border)] pb-6">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`course-tab-${c.toLowerCase()}`}
              onClick={() => setTab(c)}
              className={`border px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-all ${
                tab === c
                  ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                  : "border-[color:var(--border)] text-[color:var(--muted)] hover:border-[color:var(--primary)] hover:text-[color:var(--text)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="mt-12 divide-y divide-[color:var(--border)] border border-[color:var(--border)] bg-[color:var(--surface)]"
          >
            {COURSES[tab].map((course, i) => (
              <motion.div
                key={course.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03, duration: 0.4 }}
                data-testid={`course-row-${i}`}
                className="group grid grid-cols-12 items-center gap-6 px-6 py-6 transition-colors hover:bg-[color:var(--surface-2)] md:px-10 md:py-8"
              >
                <div className="col-span-1 font-mono text-[10px] tracking-[0.2em] text-[color:var(--muted)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-8 font-serif text-xl leading-tight md:col-span-8 md:text-3xl">
                  {course.name}
                </div>
                <div className="col-span-3 text-right font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--primary)] md:text-sm">
                  {course.duration}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
          <p className="max-w-lg text-sm text-[color:var(--muted)]">
            Every course is taught by working senior artists on live clients,
            in the same studio you already see. Enrolment is rolling —
            message us for the next batch dates.
          </p>
          <button
            data-testid="courses-enrol-btn"
            onClick={openBooking}
            className="inline-flex items-center gap-3 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] transition hover:bg-[color:var(--primary)]"
          >
            Enrol Now →
          </button>
        </div>
      </div>
    </section>
  );
};

export default Courses;
